import { useMemo } from 'react';
import type { McpServer } from '../../components/McpServerEditor';
import type { MarketplaceAsset } from '../../types/marketplace';
import { MCP_PRESETS } from './constants';
import type { GlobalTool, KnowledgeItem, PersonaTemplate, SkillItem, ToolItem, WorkflowItem } from './types';

export interface ConfigCenterAssetsInput {
  personaTemplates: PersonaTemplate[];
  skills: SkillItem[];
  workflows: WorkflowItem[];
  /** Profile 已挂载的工具（用于计算 isApplied）。 */
  tools: ToolItem[];
  globalTools: GlobalTool[];
  knowledgeItems: KnowledgeItem[];
  mcpServers: McpServer[];
}

export interface ConfigCenterAssets {
  personaAssets: MarketplaceAsset[];
  skillAssets: MarketplaceAsset[];
  workflowAssets: MarketplaceAsset[];
  toolAssets: MarketplaceAsset[];
  knowledgeAssets: MarketplaceAsset[];
  mcpAssets: MarketplaceAsset[];
}

/**
 * 六维资产的 MarketplaceAsset 视图模型组装 hook：
 * 把页面持有的原始子资源列表转换为能力市场（AssetMarketplace）渲染所需的资产数组。
 * 纯派生逻辑，不发起请求；各 useMemo 依赖与原始数据列表一一对应。
 */
export function useConfigCenterAssets({
  personaTemplates,
  skills,
  workflows,
  tools,
  globalTools,
  knowledgeItems,
  mcpServers,
}: ConfigCenterAssetsInput): ConfigCenterAssets {
  const personaAssets: MarketplaceAsset[] = useMemo(
    () =>
      personaTemplates.map((t) => ({
        id: t.id,
        type: 'persona',
        name: t.name,
        displayName: t.name,
        description: t.description,
        category: t.category,
        tags: t.tags,
        source: t.is_builtin ? 'builtin' : 'custom',
        payload: { config: t.config },
      })),
    [personaTemplates]
  );

  const skillAssets: MarketplaceAsset[] = useMemo(
    () =>
      skills.map((s) => ({
        id: s.id,
        type: 'skill',
        name: s.name,
        displayName: s.display_name,
        description: s.description || undefined,
        category: s.source,
        tags: [],
        source: (s.source as MarketplaceAsset['source']) || 'manual',
        isEnabled: s.is_enabled,
        isApplied: s.is_enabled,
        trustState: s.trust_state,
        payload: { definition: s.definition, priority: s.priority },
      })),
    [skills]
  );

  const workflowAssets: MarketplaceAsset[] = useMemo(
    () =>
      workflows.map((w) => ({
        id: w.id,
        type: 'workflow',
        name: w.name,
        displayName: w.name,
        description: w.description || undefined,
        category: 'state-machine',
        tags: [],
        source: 'manual',
        isEnabled: w.is_enabled,
        isApplied: w.is_enabled,
        payload: { entry_state: w.entry_state, states: w.states, exceptions: w.exceptions, global_vars: w.global_vars },
      })),
    [workflows]
  );

  const toolAssets: MarketplaceAsset[] = useMemo(() => {
    const attachedIds = new Set(tools.map((t) => t.tool_id));
    return globalTools.map((t) => ({
      id: t.id,
      type: 'tool',
      name: t.name,
      displayName: t.name,
      description: t.description,
      category: t.source,
      tags: [],
      source: t.source === 'builtin' ? 'builtin' : 'custom',
      isApplied: attachedIds.has(t.id),
      payload: { schema: t.schema, implementation: t.implementation },
    }));
  }, [globalTools, tools]);

  const knowledgeAssets: MarketplaceAsset[] = useMemo(
    () =>
      knowledgeItems.map((k) => ({
        id: k.id,
        type: 'knowledge',
        name: `knowledge-${k.id.slice(0, 6)}`,
        displayName: k.category ? `${k.category} · 知识条目` : '知识条目',
        description: k.content,
        category: k.category,
        tags: [],
        source: 'manual',
        isApplied: true,
        payload: { content: k.content },
      })),
    [knowledgeItems]
  );

  const mcpAssets: MarketplaceAsset[] = useMemo(() => {
    const appliedIds = new Set(mcpServers.map((s) => s.id));
    const presetAssets: MarketplaceAsset[] = MCP_PRESETS.filter((p) => !appliedIds.has(p.id)).map((p) => ({
      id: p.id,
      type: 'mcp',
      name: p.name,
      displayName: p.name,
      description: p.description,
      category: 'preset',
      tags: ['preset'],
      source: 'builtin',
      isApplied: false,
      payload: { command: p.command, args: p.args, env: p.env },
    }));
    const appliedAssets: MarketplaceAsset[] = mcpServers.map((s) => ({
      id: s.id,
      type: 'mcp',
      name: s.name,
      displayName: s.name,
      description: s.description,
      category: 'custom',
      tags: [],
      source: 'custom',
      isApplied: true,
      payload: { command: s.command, args: s.args, env: s.env },
    }));
    return [...appliedAssets, ...presetAssets];
  }, [mcpServers]);

  return { personaAssets, skillAssets, workflowAssets, toolAssets, knowledgeAssets, mcpAssets };
}
